import {
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spacer,
  Stack,
} from '@chakra-ui/react';
import * as React from 'react';
import { MoreFilters } from '@/components/MoreFilters';
import { EAttributeOwnerKind } from '@/graphql-types/globalTypes';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { SearchTips } from '@/imports/ui/components/algolia/SearchTips';
import {
  Strawberry,
  StrawberryAside,
  StrawberryMain,
} from '@/layouts/Strawberry';
import { Hits } from './components/Hits/Hits';
import { MemberDirectoryResultsCount } from './components/MemberDirectoryResultsCount';
import { MemberDirectorySelectedFilters } from './components/MemberDirectorySelectedFilters';
import { useMemberDirectorySearch } from './hooks';

export const MemberDirectoryPage: React.FC = () => {
  const memberManagement = useMemberManagement();
  const directory = useMemberDirectorySearch();

  if (directory.isInitialLoading || memberManagement.loading) {
    return (
      <Strawberry>
        <StrawberryMain>
          <Loading />
        </StrawberryMain>
        <StrawberryAside />
      </Strawberry>
    );
  }

  if (directory.memberDirectorySearch.error) {
    return (
      <Strawberry>
        <StrawberryMain>
          <UnexpectedError />
        </StrawberryMain>
        <StrawberryAside />
      </Strawberry>
    );
  }

  return (
    <Strawberry>
      <StrawberryMain>
        <Stack spacing="4">
          <Card>
            <Stack spacing="0" divider={<Divider />}>
              <Flex py="4" pl="4">
                <InputGroup flex="1">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="primary.500" />
                  </InputLeftElement>
                  <Input
                    variant="outline"
                    borderRadius="md"
                    placeholder="Search Individuals"
                    type="search"
                    value={directory.searchText}
                    onChange={(event) =>
                      directory.setSearchText(event.target.value)
                    }
                  />
                </InputGroup>
                <SearchTips ownerKind={EAttributeOwnerKind.USER} />
              </Flex>

              {(directory.selectedFilters.length > 0 ||
                directory.unselectedFilters.length > 0) && (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4" p="4">
                  <MemberDirectorySelectedFilters
                    selectedFilters={directory.selectedFilters}
                    unselectFilter={directory.unselectFilter}
                    selectFilter={directory.selectFilter}
                    freeTextValues={directory.freeTextValues}
                    onFreeTextValueChange={directory.setFreeTextValue}
                  />

                  <Flex direction="column">
                    <Spacer />
                    <MoreFilters
                      unselectedFilters={directory.unselectedFilters}
                      selectedFilters={directory.selectedFilters}
                      selectFilter={directory.selectFilter}
                      unselectFilter={directory.unselectFilter}
                    />
                  </Flex>
                </SimpleGrid>
              )}
            </Stack>
          </Card>

          <Card px="4" py="6" pos="relative">
            <MemberDirectoryResultsCount totalCount={directory.totalCount} />
            <Hits
              members={directory.results}
              loading={directory.isLoading && directory.results.length === 0}
              hasMore={directory.hasMore}
              isLoadingMore={directory.isLoadingMore}
              onLoadMore={directory.loadMore}
            />
          </Card>
        </Stack>
      </StrawberryMain>
      <StrawberryAside />
    </Strawberry>
  );
};
